import { Injectable, inject } from '@angular/core';
import { environment } from '@env';
import type { TranslateLoader, TranslationObject } from '@ngx-translate/core';
import { type Observable, from } from 'rxjs';
import { CACHE_STORAGE } from '@tvd/cdk/browser';
import { CACHED_TRANSLATE_LOADER_CONFIG } from './translate-loader-config';

const CACHE_NAME = 'zynday-translations-v1';
const VERSION_HEADER = 'X-Version';
const FETCH_TIMEOUT_MS = 10_000;

@Injectable()
export class CachedTranslateLoader implements TranslateLoader {
  private readonly config = inject(CACHED_TRANSLATE_LOADER_CONFIG);
  private readonly caches = inject(CACHE_STORAGE);
  private readonly refreshing = new Map<string, Promise<void>>();

  getTranslation(lang: string): Observable<TranslationObject> {
    return from(this.loadTranslation(lang));
  }

  private async loadTranslation(lang: string): Promise<TranslationObject> {
    const route = environment.envName === 'production' ? '/public' : '/admin';
    const url = `${environment.apiUrl}${route}/translations/languages/${lang}/files/${this.config.fileName ?? 'translations.json'}`;
    const cache = await this.caches.open(CACHE_NAME);

    // Local dev: re-seeding the local R2 bucket does NOT bump the version
    // metadata, so the version-gated background refresh (below) would serve a
    // stale blob forever — newly-added keys render as raw keys until the cache
    // is manually cleared. Always fetch fresh locally so a re-seed + reload is
    // enough. Staging/prod keep the cache-first behaviour.
    if (environment.envName === 'local') {
      return this.fetchFromNetwork(url, cache);
    }

    const cached = await cache.match(url);

    if (cached) {
      this.backgroundRefresh(url, cache, cached);
      const json = await cached.clone().json();
      return json.data;
    }

    return this.fetchFromNetwork(url, cache);
  }

  private async fetchFromNetwork(
    url: string,
    cache: Cache,
  ): Promise<TranslationObject> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      await cache.put(url, response.clone());

      const json = await response.json();
      return json.data;
    } catch {
      return this.staticFallback(url);
    }
  }

  private async staticFallback(
    originalUrl: string,
  ): Promise<TranslationObject> {
    const lang = this.extractLang(originalUrl);
    try {
      const response = await fetch(`./i18n/${lang}/${lang}.json`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch {
      return {};
    }
  }

  private backgroundRefresh(
    url: string,
    cache: Cache,
    cachedResponse: Response,
  ): void {
    if (this.refreshing.has(url)) return;

    const promise = (async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      try {
        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (!response.ok) return;

        const cachedVersion = cachedResponse.headers.get(VERSION_HEADER);
        const freshVersion = response.headers.get(VERSION_HEADER);

        if (
          cachedVersion !== null &&
          freshVersion !== null &&
          cachedVersion === freshVersion
        ) {
          return;
        }

        await cache.put(url, response);
      } catch {
        // Silent failure -- cache remains valid
      }
    })().finally(() => this.refreshing.delete(url));

    this.refreshing.set(url, promise);
  }

  private extractLang(url: string): string {
    const match = url.match(/\/languages\/([^/]+)\//);
    return match?.[1] ?? 'de';
  }
}
